import React, { useState, useEffect } from 'react';
import {
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  InformationCircleIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const AddToolsPage = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [testingTool, setTestingTool] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [isExecutingTest, setIsExecutingTest] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    http_method: 'GET',
    url: '',
    headers: [],
    body_template: {},
    parameters: [],
    response_mapping: '',
    timeout_seconds: 30
  });

  const [testParams, setTestParams] = useState({});

  // Helper functions for managing headers
  const addHeader = () => {
    setFormData(prev => ({
      ...prev,
      headers: [...prev.headers, { key: '', value: '', sensitive: false }]
    }));
  };

  const updateHeader = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.map((header, i) => 
        i === index ? { ...header, [field]: value } : header
      )
    }));
  };

  const removeHeader = (index) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index)
    }));
  };

  // Helper functions for managing body template fields
  const addBodyField = () => {
    const fieldName = `field_${Object.keys(formData.body_template).length + 1}`;
    setFormData(prev => ({
      ...prev,
      body_template: { ...prev.body_template, [fieldName]: '' }
    }));
  };

  const updateBodyField = (oldKey, newKey, value) => {
    setFormData(prev => {
      const newBodyTemplate = { ...prev.body_template };
      if (oldKey !== newKey && oldKey in newBodyTemplate) {
        delete newBodyTemplate[oldKey];
      }
      newBodyTemplate[newKey] = value;
      return { ...prev, body_template: newBodyTemplate };
    });
  };

  const removeBodyField = (key) => {
    setFormData(prev => {
      const newBodyTemplate = { ...prev.body_template };
      delete newBodyTemplate[key];
      return { ...prev, body_template: newBodyTemplate };
    });
  };

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await api.getTools();
      setTools(response.data.tools || []);
    } catch (error) {
      console.error('Error fetching tools:', error);
      toast.error('Failed to load tools');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      http_method: 'GET',
      url: '',
      headers: [],
      body_template: {},
      parameters: [],
      response_mapping: '',
      timeout_seconds: 30
    });
    setEditingTool(null);
  };

  const handleShowModal = (tool = null) => {
    if (tool) {
      setEditingTool(tool);
      
      // Convert headers - handle both old format (object) and new format (array)
      let headersArray = [];
      if (tool.headers) {
        if (Array.isArray(tool.headers)) {
          // New format: ensure sensitive field exists
          headersArray = tool.headers.map(header => ({
            ...header,
            sensitive: header.sensitive || false
          }));
        } else {
          // Old format: convert object to array
          headersArray = Object.entries(tool.headers).map(([key, value]) => ({ 
            key, 
            value, 
            sensitive: false 
          }));
        }
      }
      
      // Ensure parameters have default and sensitive fields for backward compatibility
      const parametersWithDefaults = (tool.parameters || []).map(param => ({
        ...param,
        default: param.default || '',
        sensitive: param.sensitive || false
      }));
      
      setFormData({
        name: tool.name,
        description: tool.description || '',
        http_method: tool.http_method,
        url: tool.url,
        headers: headersArray,
        body_template: tool.body_template || {},
        parameters: parametersWithDefaults,
        response_mapping: tool.response_mapping ? JSON.stringify(tool.response_mapping, null, 2) : '',
        timeout_seconds: tool.timeout_seconds || 30
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Keep headers as array format to preserve sensitive flag
      const headers = formData.headers.filter(header => header.key && header.value);
      
      // Handle body template - keep as object if not empty
      const body_template = Object.keys(formData.body_template).length > 0 ? formData.body_template : null;
      
      // Parse response mapping if provided
      let response_mapping = null;
      if (formData.response_mapping) {
        try {
          response_mapping = JSON.parse(formData.response_mapping);
        } catch (error) {
          toast.error('Invalid JSON in response mapping field');
          return;
        }
      }

      const toolData = {
        ...formData,
        headers,
        body_template,
        response_mapping,
        parameters: formData.parameters
      };

      if (editingTool) {
        await api.updateTool(editingTool.id, toolData);
        toast.success('Tool updated successfully!');
      } else {
        await api.createTool(toolData);
        toast.success('Tool created successfully!');
      }

      handleCloseModal();
      fetchTools();
    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error(error.response?.data?.error || 'Failed to save tool');
    }
  };

  const handleDelete = async (tool) => {
    if (!window.confirm(`Are you sure you want to delete "${tool.name}"?`)) {
      return;
    }

    try {
      await api.deleteTool(tool.id);
      toast.success('Tool deleted successfully!');
      fetchTools();
    } catch (error) {
      console.error('Error deleting tool:', error);
      toast.error('Failed to delete tool');
    }
  };

  const handleTestTool = async (tool) => {
    setTestingTool(tool);
    setTestResults(null);
    
    // Initialize test parameters based on tool parameters
    const initialParams = {};
    if (tool.parameters && Array.isArray(tool.parameters)) {
      tool.parameters.forEach(param => {
        const defaultValue = param.default !== undefined && param.default !== null ? param.default : '';
        initialParams[param.name] = defaultValue;
      });
    }
    setTestParams(initialParams);
  };

  const executeTest = async () => {
    setIsExecutingTest(true);
    try {
      const response = await api.testTool(testingTool.id, testParams);
      setTestResults(response.data);
      toast.success('Tool test executed successfully!');
    } catch (error) {
      console.error('Error testing tool:', error);
      setTestResults({
        success: false,
        error: error.response?.data?.error || 'Test failed',
        execution_time_ms: 0
      });
      toast.error('Tool test failed');
    } finally {
      setIsExecutingTest(false);
    }
  };

  const addParameter = () => {
    setFormData(prev => ({
      ...prev,
      parameters: [...prev.parameters, { name: '', type: 'string', description: '', required: true, default: '', sensitive: false }]
    }));
  };

  const updateParameter = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.map((param, i) => 
        i === index ? { ...param, [field]: value } : param
      )
    }));
  };

  const removeParameter = (index) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <WrenchScrewdriverIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Custom HTTP Tools</h1>
                  <p className="text-sm text-gray-600">Create and manage custom HTTP tools for your AI agents</p>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={fetchTools}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Refresh
                </button>
                <button
                  onClick={() => handleShowModal()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-whatsapp-600 hover:bg-whatsapp-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Tool
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-blue-400" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">About Custom Tools</h4>
              <p className="mt-1 text-sm text-blue-700">
                Create custom HTTP tools that your AI agents can use to interact with external APIs. 
                Tools support GET and POST requests with custom headers, parameters, and response mapping.
              </p>
            </div>
          </div>
        </div>

        {/* Tools Grid */}
        {tools.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tools yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first custom HTTP tool.</p>
            <div className="mt-6">
              <button
                onClick={() => handleShowModal()}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-whatsapp-600 hover:bg-whatsapp-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Tool
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool) => (
              <div key={tool.id} className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      tool.http_method === 'GET' ? 'bg-green-100 text-green-800' :
                      tool.http_method === 'POST' ? 'bg-blue-100 text-blue-800' :
                      tool.http_method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                      tool.http_method === 'DELETE' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {tool.http_method}
                    </span>
                    <span className={`w-2 h-2 rounded-full ${tool.is_active ? 'bg-green-400' : 'bg-gray-400'}`}></span>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleTestTool(tool)}
                      className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                      title="Test tool"
                    >
                      <PlayIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleShowModal(tool)}
                      className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                      title="Edit tool"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(tool)}
                      className="p-1 text-red-600 hover:text-red-800 transition-colors"
                      title="Delete tool"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-lg font-medium text-gray-900 mb-2">{tool.name}</h3>
                {tool.description && (
                  <p className="text-sm text-gray-600 mb-3">{tool.description}</p>
                )}
                
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">URL:</span>
                    <span className="ml-2 text-gray-600 break-all">{tool.url}</span>
                  </div>
                  {tool.parameters && tool.parameters.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Parameters:</span>
                      <span className="ml-2 text-gray-600">{tool.parameters.length} defined</span>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-700">Timeout:</span>
                    <span className="ml-2 text-gray-600">{tool.timeout_seconds}s</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                  Created: {new Date(tool.created_at).toLocaleDateString()}
                  {tool.created_by_name && (
                    <span> by {tool.created_by_name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Tool Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingTool ? 'Edit Tool' : 'Create New Tool'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tool Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="My Custom Tool"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HTTP Method <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.http_method}
                      onChange={(e) => setFormData({...formData, http_method: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="DELETE">DELETE</option>
                      <option value="PATCH">PATCH</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="What does this tool do?"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="https://api.example.com/endpoint"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use {"{parameter_name}"} for dynamic values (e.g., https://api.example.com/users/{"{user_id}"})
                  </p>
                </div>

                {/* Headers Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Request Headers
                    </label>
                    <button
                      type="button"
                      onClick={addHeader}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Add Header
                    </button>
                  </div>
                  
                  {formData.headers.length === 0 ? (
                    <div className="text-sm text-gray-500 py-4 text-center border-2 border-dashed border-gray-300 rounded-lg">
                      No headers defined. Click "Add Header" to add custom headers.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {formData.headers.map((header, index) => (
                        <div key={index} className="space-y-2 p-3 border border-gray-200 rounded-lg">
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <input
                                type="text"
                                placeholder="Header name (e.g., Authorization)"
                                value={header.key}
                                onChange={(e) => updateHeader(index, 'key', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                              />
                            </div>
                            <div className="flex-1">
                              <input
                                type={header.sensitive ? "password" : "text"}
                                placeholder="Header value (e.g., Bearer {token})"
                                value={header.value}
                                onChange={(e) => updateHeader(index, 'value', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeHeader(index)}
                              className="px-2 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                              title="Remove header"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={header.sensitive}
                                onChange={(e) => updateHeader(index, 'sensitive', e.target.checked)}
                                className="mr-2 rounded border-gray-300 text-whatsapp-600 focus:ring-whatsapp-500"
                              />
                              <span className="flex items-center">
                                🔒 Sensitive (hidden from AI, password field)
                              </span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Use {"{"}parameter_name{"}"} syntax for dynamic values (e.g., Bearer {"{"}token{"}"}).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.timeout_seconds}
                    onChange={(e) => setFormData({...formData, timeout_seconds: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    min="1"
                    max="300"
                  />
                </div>

                {['POST', 'PUT', 'PATCH'].includes(formData.http_method) && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Request Body Template
                      </label>
                      <button
                        type="button"
                        onClick={addBodyField}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Add Field
                      </button>
                    </div>
                    
                    {Object.keys(formData.body_template).length === 0 ? (
                      <div className="text-sm text-gray-500 py-4 text-center border-2 border-dashed border-gray-300 rounded-lg">
                        No body fields defined. Click "Add Field" to add JSON fields for the request body.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(formData.body_template).map(([key, value], index) => (
                          <div key={index} className="flex gap-2 p-3 border border-gray-200 rounded-lg">
                            <div className="flex-1">
                              <input
                                type="text"
                                placeholder="Field name (e.g., message)"
                                value={key}
                                onChange={(e) => updateBodyField(key, e.target.value, value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                              />
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                placeholder="Field value (e.g., {text})"
                                value={value}
                                onChange={(e) => updateBodyField(key, key, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeBodyField(key)}
                              className="px-2 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                              title="Remove field"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Use {"{"}parameter_name{"}"} syntax for dynamic values (e.g., {"{"}text{"}"}, {"{"}user_id{"}"}).
                    </p>
                  </div>
                )}

                {/* Parameters Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Parameters
                    </label>
                    <button
                      type="button"
                      onClick={addParameter}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Add Parameter
                    </button>
                  </div>
                  
                  {formData.parameters.map((param, index) => (
                    <div key={index} className="space-y-2 mb-4 p-3 border border-gray-200 rounded">
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-3">
                          <label className="block text-xs text-gray-600 mb-1">Parameter name</label>
                          <input
                            type="text"
                            placeholder="e.g., user_id"
                            value={param.name}
                            onChange={(e) => updateParameter(index, 'name', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-600 mb-1">Type</label>
                          <select
                            value={param.type}
                            onChange={(e) => updateParameter(index, 'type', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                          </select>
                        </div>
                        <div className="col-span-3">
                          <label className="block text-xs text-gray-600 mb-1">Default value</label>
                          <input
                            type="text"
                            placeholder="Default value"
                            value={param.default || ''}
                            onChange={(e) => updateParameter(index, 'default', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-600 mb-1">Options</label>
                          <div className="space-y-1">
                            <label className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={param.required}
                                onChange={(e) => updateParameter(index, 'required', e.target.checked)}
                                className="mr-1"
                              />
                              Required
                            </label>
                            <label className="flex items-center text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={param.sensitive}
                                onChange={(e) => updateParameter(index, 'sensitive', e.target.checked)}
                                className="mr-1 rounded border-gray-300 text-whatsapp-600 focus:ring-whatsapp-500"
                              />
                              🔒 Sensitive
                            </label>
                          </div>
                        </div>
                        <div className="col-span-2 flex items-end">
                          <button
                            type="button"
                            onClick={() => removeParameter(index)}
                            className="px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded text-sm"
                            title="Remove parameter"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Description</label>
                        <input
                          type="text"
                          placeholder="Parameter description"
                          value={param.description || ''}
                          onChange={(e) => updateParameter(index, 'description', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Response Mapping (JSON)
                  </label>
                  <textarea
                    value={formData.response_mapping}
                    onChange={(e) => setFormData({...formData, response_mapping: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 font-mono text-sm"
                    placeholder='{"result": "$.data.result", "status": "$.status"}'
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Use JSONPath expressions to map response data
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-whatsapp-600 border border-transparent rounded-md hover:bg-whatsapp-700"
                  >
                    {editingTool ? 'Update Tool' : 'Create Tool'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Test Tool Modal */}
        {testingTool && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Test Tool: {testingTool.name}
                </h3>
                <button
                  onClick={() => {setTestingTool(null); setTestResults(null);}}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Parameters Input */}
                {testingTool.parameters && testingTool.parameters.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {testingTool.parameters.map((param) => (
                        <div key={param.name}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {param.name} {param.required && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type={param.sensitive ? 'password' : (param.type === 'number' ? 'number' : 'text')}
                            value={testParams[param.name] || ''}
                            onChange={(e) => setTestParams({...testParams, [param.name]: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={param.description}
                            required={param.required}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Execute Button */}
                <div className="flex justify-center">
                  <button
                    onClick={executeTest}
                    disabled={isExecutingTest}
                    className={`px-6 py-2 text-white rounded-md flex items-center ${
                      isExecutingTest 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isExecutingTest ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Executing...
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-4 w-4 mr-2" />
                        Execute Test
                      </>
                    )}
                  </button>
                </div>

                {/* Test Results */}
                {testResults && (
                  <div className="border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Test Results</h4>
                    <div className={`p-4 rounded-md ${testResults.success ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="flex items-center mb-2">
                        {testResults.success ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                        ) : (
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                        )}
                        <span className={`text-sm font-medium ${testResults.success ? 'text-green-800' : 'text-red-800'}`}>
                          {testResults.success ? 'Success' : 'Failed'}
                        </span>
                        <span className="text-sm text-gray-600 ml-4">
                          Execution time: {testResults.execution_time_ms}ms
                        </span>
                      </div>
                      
                      {testResults.success && testResults.response && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">
                            Status: {testResults.response.status_code}
                          </div>
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">Response:</span>
                            <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-48">
                              {JSON.stringify(testResults.response.data, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                      
                      {testResults.error && (
                        <div className="text-sm text-red-700">
                          <span className="font-medium">Error:</span> {testResults.error}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddToolsPage;